/**
 * unitClips — the ordered, de-duplicated list of every audio clip in a unit,
 * each with its English text, Georgian, and which voice should say it.
 * Shared by the audio-review page and the audio-recorder page so they stay in
 * lockstep. (Dev/QA tooling for the human-audio recording effort, 2026-06-14.)
 */
import type { Lesson } from '@fluent/types';
import { phraseChoiceClips } from '../lesson/phraseChoiceText.js';
import { getUnit, getSkill } from './course.js';
import { getLesson } from './lessons.js';

export type ClipVoice = 'Narrator' | 'Giorgi' | 'Mariam';

export interface UnitClip {
  key: string;
  en: string;
  ka: string;
  voice: ClipVoice;
  lessonPos: number;
  skill: string;
  lessonTitle: string;
}

const SKILL_LABEL: Record<string, string> = {
  'skill-01-greetings': 'greetings',
  'skill-02-to-be': 'to be',
  'skill-03-pronouns': 'pronouns',
  'skill-04-yes-no': 'yes/no',
  'skill-25-questions': 'questions',
};

function rawClips(lesson: Lesson): { key: string; en: string; ka: string; voice: ClipVoice }[] {
  const out: { key: string; en: string; ka: string; voice: ClipVoice }[] = [];
  for (const ex of lesson.exercises) {
    if (ex.type === 'tap_pairs') {
      for (const p of ex.pairs) if (p.audio_key) out.push({ key: p.audio_key, en: p.en, ka: p.ka, voice: 'Narrator' });
    } else if (ex.type === 'phrase_intro') {
      const voice: ClipVoice = ex.speaker === 'Giorgi' ? 'Giorgi' : ex.speaker === 'Mariam' ? 'Mariam' : 'Narrator';
      out.push({ key: ex.audio_key, en: ex.en, ka: ex.ka, voice });
      // Phrases course: the card's example sentence has its own clip, read
      // by the card's voice (scripts/tts-text.ts exerciseClips).
      if (ex.example_audio_key && ex.example_en) {
        out.push({ key: ex.example_audio_key, en: ex.example_en, ka: ex.example_ka ?? '', voice });
      }
    } else if (ex.type === 'dialogue') {
      for (const l of ex.lines) out.push({ key: l.audio_key, en: l.en, ka: l.ka, voice: l.speaker === 'Giorgi' ? 'Giorgi' : 'Mariam' });
    } else if (ex.type === 'listen_type' || ex.type === 'listen_choose') {
      out.push({ key: ex.audio_key, en: ex.answer, ka: ex.translation_ka ?? '', voice: 'Narrator' });
    } else if (ex.type === 'translate_typed' || ex.type === 'choose_correct') {
      if (ex.audio_key) out.push({ key: ex.audio_key, en: ex.answer, ka: ex.prompt ?? '', voice: 'Narrator' });
    } else if (ex.type === 'cloze') {
      if (ex.audio_key) out.push({ key: ex.audio_key, en: ex.prompt_en.replace('___', ex.answer), ka: ex.prompt_ka, voice: 'Narrator' });
    } else if (ex.type === 'phrase_choice') {
      // A prompt with a speaker is read in that character's voice.
      for (const c of phraseChoiceClips(ex)) {
        out.push({ key: c.key, en: c.en, ka: c.ka, voice: c.speaker ?? 'Narrator' });
      }
    }
  }
  return out;
}

export function unitClips(unitId: string): UnitClip[] {
  const unit = getUnit(unitId);
  const seen = new Set<string>();
  const result: UnitClip[] = [];
  let pos = 0;
  for (const skillId of unit?.skills ?? []) {
    const skill = getSkill(skillId);
    for (const lessonId of skill?.lessons ?? []) {
      pos += 1;
      const lesson = getLesson(lessonId);
      if (!lesson) continue;
      for (const c of rawClips(lesson)) {
        if (seen.has(c.key)) continue;
        seen.add(c.key);
        result.push({
          ...c,
          lessonPos: pos,
          skill: SKILL_LABEL[skillId] ?? skillId,
          lessonTitle: lesson.title_en,
        });
      }
    }
  }
  return result;
}
